import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { checkResetToken, resetPassword } from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import { authErrorMessage } from '../auth/errors';
import { forgetLinkToken, takeLinkToken } from '../auth/linkToken';
import { PasswordField } from '../components/PasswordField';

const STORAGE_KEY = 'vbbplay.reset_token';

type Phase = 'checking' | 'invalid' | 'form' | 'done';

export function ResetPasswordPage() {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const { user, refresh } = useAuth();
  const [token] = useState(() => takeLinkToken(STORAGE_KEY));
  const [phase, setPhase] = useState<Phase>(token ? 'checking' : 'invalid');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    let active = true;
    void checkResetToken(token)
      .then((valid) => active && setPhase(valid ? 'form' : 'invalid'))
      .catch(() => active && setPhase('invalid'));
    return () => {
      active = false;
    };
  }, [token]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!token) return;
    setError(null);
    if (password !== confirm) {
      setError(t('auth:passwordsDontMatch'));
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      forgetLinkToken(STORAGE_KEY);
      setPhase('done');
      if (user) await refresh();
    } catch (err) {
      setError(authErrorMessage(t, err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page auth-page auth-sheet-page">
      <article className="auth-card auth-sheet">
        <h1>{t('auth:resetTitle')}</h1>
        {phase === 'checking' ? <p className="muted-note">{t('auth:checking')}</p> : null}
        {phase === 'invalid' ? (
          <>
            <p className="auth-error" role="alert">
              {t('auth:linkInvalid')}
            </p>
            <Link to="/auth/forgot" className="btn btn-primary btn-block">
              {t('auth:requestNewLink')}
            </Link>
          </>
        ) : null}
        {phase === 'done' ? (
          <>
            <p className="auth-success" role="status">
              {t('auth:resetDone')}
            </p>
            <Link to="/auth/login" className="btn btn-primary btn-block">
              {t('auth:backToLogin')}
            </Link>
          </>
        ) : null}
        {phase === 'form' ? (
          <form className="auth-form" onSubmit={onSubmit}>
            <PasswordField
              label={t('auth:newPassword')}
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              minLength={8}
            />
            <PasswordField
              label={t('auth:confirmPassword')}
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              minLength={8}
              invalid={confirm.length > 0 && confirm !== password}
            />
            <p className="muted-note">{t('auth:passwordRules')}</p>
            {error ? (
              <p className="auth-error" role="alert">
                {error}
              </p>
            ) : null}
            <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
              {submitting ? t('common:loading') : t('auth:resetCta')}
            </button>
          </form>
        ) : null}
      </article>
    </div>
  );
}
